<template>
  <div class="company-selector">
    <!-- Selected companies as removable badges -->
    <div
      v-if="selectedCompanies.length > 0"
      class="d-flex flex-wrap gap-2 mb-2"
      data-testid="company-selector-badges"
    >
      <span
        v-for="company in selectedCompanies"
        :key="company.value"
        class="badge bg-primary-subtle text-primary-emphasis d-inline-flex align-items-center gap-2 py-2"
      >
        <span>{{ company.label }}</span>
        <button
          type="button"
          class="btn-close btn-close-sm"
          :aria-label="$t('common.remove')"
          data-testid="company-selector-remove"
          @click="remove(company.value)"
        />
      </span>
    </div>

    <!-- Search-and-add control; hidden once the cap is reached (the label states the cap). -->
    <SearchableSelectComponent
      v-if="!maxReached"
      :key="addKey"
      :options="visibleOptions"
      :modelValue="null"
      :isLoading="companiesLoading"
      :isError="companiesError"
      :placeholder="$t('common.search')"
      :size="size"
      autoOpen
      clearOnFocus
      @update:modelValue="onPick"
      @queryUpdated="query = $event"
    />
  </div>
</template>

<script setup lang="ts">
import {
  fetchAdminCompanies,
  type AdminCompany,
} from "@/components/common/adminCompaniesApi";
import SearchableSelectComponent from "@/components/common/SearchableSelectComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Multi-company selector: selected companies show as removable badges, and a
 * searchable dropdown adds more (up to an optional cap). Fetches company options
 * itself and normalizes the v-model to a distinct list of company ids.
 */
const props = withDefaults(
  defineProps<{
    /** Visual size of the search control */
    size?: "sm" | "md";
    /** Optional selection cap (0 = unlimited); the search hides once reached */
    max?: number;
  }>(),
  {
    size: "md",
    max: 0,
  }
);

const selectedCompanyIds = defineModel<number[]>({ default: () => [] });

const emit = defineEmits<{
  error: [error: unknown];
}>();

interface CompanyOption {
  label: string;
  value: number;
  description?: string;
}

// PERF: Cap how many option rows are ever handed to the dropdown. Rendering the full company list
// (hundreds of rows, each a highlighter component) makes the dropdown sluggish; the user narrows
// with the search box instead.
const MAX_RENDERED_OPTIONS = 50;

const companiesLoading = ref(false);
const companiesError = ref(false);
const availableCompanies = ref<AdminCompany[]>([]);
// Current search text, mirrored from the dropdown so we can pre-filter before rendering.
const query = ref("");
// WORKAROUND: Bumped after each add to remount the search control, clearing its input.
const addKey = ref(0);
const { globalErrorHandler } = useGlobalHandlers();
const { t } = useI18n();

/**
 * Loads the company options into `availableCompanies` (see `fetchAdminCompanies`).
 *
 * On failure (non-200, empty body, or network error) the error is passed to
 * `globalErrorHandler`, re-emitted as an `error` event for the parent, and
 * `companiesError` is set so the template can show the retry state.
 */
const fetchCompanies = async (): Promise<void> => {
  try {
    companiesLoading.value = true;
    companiesError.value = false;
    availableCompanies.value = await fetchAdminCompanies();
  } catch (error) {
    globalErrorHandler?.(error);
    emit("error", error);
    companiesError.value = true;
  } finally {
    companiesLoading.value = false;
  }
};

onMounted(() => {
  fetchCompanies();
});

defineExpose({ fetchCompanies });

const toOption = (company: AdminCompany): CompanyOption => ({
  label: company.deleted_at
    ? `${company.name} ${t("common.deleted")}`
    : company.name,
  value: company.id,
  description: `${company.country} - ${company.id}`,
});

const optionById = computed<Map<number, CompanyOption>>(
  () => new Map(availableCompanies.value.map((c) => [c.id, toOption(c)]))
);

const maxReached = computed(
  () => props.max > 0 && selectedCompanyIds.value.length >= props.max
);

// Selected ids resolved to display options; unknown ids fall back to their id.
const selectedCompanies = computed<CompanyOption[]>(() =>
  selectedCompanyIds.value.map(
    (id) => optionById.value.get(id) ?? { label: `#${id}`, value: id }
  )
);

// Only offer companies that are not already selected.
const availableOptions = computed<CompanyOption[]>(() => {
  const selected = new Set(selectedCompanyIds.value);
  return availableCompanies.value
    .filter((c) => !selected.has(c.id))
    .map(toOption);
});

// PERF: What the dropdown actually renders: the query matches (all terms must appear in label or
// description), capped at MAX_RENDERED_OPTIONS so the option list stays small and fast. With no
// query we still show the first slice so the control is browsable on focus.
const visibleOptions = computed<CompanyOption[]>(() => {
  const terms = query.value.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matches = terms.length
    ? availableOptions.value.filter((option) => {
        const haystack =
          `${option.label} ${option.description ?? ""}`.toLowerCase();
        return terms.every((term) => haystack.includes(term));
      })
    : availableOptions.value;
  return matches.slice(0, MAX_RENDERED_OPTIONS);
});

const onPick = (value: number | string | null | undefined): void => {
  const id = Number(value);
  if (
    value != null &&
    Number.isInteger(id) &&
    !maxReached.value &&
    !selectedCompanyIds.value.includes(id)
  ) {
    selectedCompanyIds.value = [...selectedCompanyIds.value, id];
  }
  // WORKAROUND: Reset the query and remount the search control so its input clears, ready for the next pick.
  query.value = "";
  addKey.value += 1;
};

const remove = (id: number): void => {
  selectedCompanyIds.value = selectedCompanyIds.value.filter((x) => x !== id);
};
</script>

<style scoped lang="scss">
.company-selector {
  width: 100%;
}
</style>
